// PATH: src/pages/admin/tabs/EventsTab.jsx
// VERSION: 2.1 — Refactored for Dark Mode Support

import React, { useEffect, useState } from 'react';
import {
  Box, Button, Flex, Input, Table, Thead, Tbody, Tr, Th, Td, useToast, Heading, Spinner, Textarea, SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiRefreshCw, FiEdit2, FiTrash2, FiSave } from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';
import CloudinaryImageField from '../../../admin/components/CloudinaryImageField';

export default function EventsTab() {
  const toast = useToast();

  // --- Theme Variables ---
  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('brand.200', 'whiteAlpha.300');
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputBorder = useColorModeValue('brand.300', 'whiteAlpha.300');
  const tableBorderColor = useColorModeValue('brand.200', 'whiteAlpha.300');

  // --- BW Style Object (Dynamic) ---
  const bw = {
    box: {
      border: '1px solid',
      borderColor: borderColor,
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: inputBorder,
      bg: inputBg,
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'text.muted'), fontWeight: 'semibold' },
      _hover: { borderColor: useColorModeValue('brand.400', 'whiteAlpha.400') },
      _focus: { borderColor: 'accent.500', boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)' },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: { bg: 'accent.600', borderColor: 'accent.600', transform: 'translateY(-1px)', boxShadow: 'md' },
      _active: { transform: 'translateY(0)', boxShadow: 'sm' },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: 'transparent',
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'whiteAlpha.300'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: { bg: useColorModeValue('brand.50', 'whiteAlpha.100'), borderColor: useColorModeValue('brand.400', 'whiteAlpha.400') },
    },
  };

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({
    id: null,
    title: '',
    date: '',
    endDate: '',
    time: '',
    place: '',
    description: '',
    image: '',     // publicId o URL (portada)
    key: '',       // clave para enlazar álbum
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/events', { params: { limit: 500, offset: 0 } });
      // Acepta {items:[]}|{events:[]}|[]
      const items = Array.isArray(data?.items) ? data.items
        : Array.isArray(data?.events) ? data.events
          : Array.isArray(data) ? data : [];
      setEvents(items);
    } catch {
      toast({ status: 'error', title: 'Error cargando eventos' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Helper to format ISO date to YYYY-MM-DD for input fields
  function formatDateForInput(isoDate) {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  }

  function editRow(r) {
    console.log('editRow received:', r);
    console.log('end_date value:', r.end_date);
    console.log('formatted endDate:', formatDateForInput(r.end_date));
    setForm({
      id: r.id,
      title: r.title || '',
      date: formatDateForInput(r.date),
      endDate: formatDateForInput(r.end_date),
      time: r.time || '',
      place: r.place || '',
      description: r.description || '',
      image: r.image || '',
      key: (r.key || `${r.date || ''}|${r.title || ''}`).trim(),
    });
  }

  function resetForm() {
    setForm({
      id: null, title: '', date: '', endDate: '', time: '', place: '', description: '', image: '', key: '',
    });
  }

  async function save() {
    const payload = {
      title: form.title.trim(),
      date: form.date || null,
      endDate: form.endDate || null,
      time: form.time || null,
      place: form.place || null,
      description: form.description || null,
      image: form.image || null, // publicId o URL
      key: (form.key || '').trim() || null,
    };
    if (!payload.title) {
      toast({ status: 'warning', title: 'Título requerido' });
      return;
    }
    try {
      if (form.id) {
        await axiosInstance.patch(`/events/${form.id}`, payload);
        toast({ status: 'success', title: 'Evento actualizado' });
      } else {
        await axiosInstance.post('/events', payload);
        toast({ status: 'success', title: 'Evento creado' });
      }
      resetForm();
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo guardar' });
    }
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar evento?')) return;
    try {
      await axiosInstance.delete(`/events/${id}`);
      toast({ status: 'success', title: 'Eliminado' });
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>Eventos</Heading>
      <Box fontSize="sm" mb={4} color={subTextColor}>
        Crea/edita eventos. La portada puede subirse a Cloudinary y se mostrará en Home/Próximos eventos.
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3} mb={3}>
        <Input {...bw.input} placeholder="Título" value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        <Input {...bw.input} placeholder="Fecha Inicio (YYYY-MM-DD)" value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
        <Input {...bw.input} placeholder="Fecha Fin (YYYY-MM-DD)" value={form.endDate}
          onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} />
        <Input {...bw.input} placeholder="Hora (opcional)" value={form.time}
          onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} />
        <Input {...bw.input} placeholder="Lugar (opcional)" value={form.place}
          onChange={(e) => setForm((f) => ({ ...f, place: e.target.value }))} />
        <Input {...bw.input} placeholder="Event Key (para enlazar álbum)" value={form.key}
          onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))} />
      </SimpleGrid>

      <Textarea
        {...bw.input}
        rows={3}
        placeholder="Descripción (opcional)"
        value={form.description}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        mb={3}
      />

      <Box mb={3}>
        <CloudinaryImageField
          label="Portada (Cloudinary)"
          value={form.image}
          onChange={(v) => setForm((f) => ({ ...f, image: v }))}
          folder="events"
          previewW={420}
          previewH={240}
        />
      </Box>

      <Flex gap={2} mb={4} wrap="wrap">
        <Button {...bw.btn} leftIcon={<FiSave />} onClick={save}>
          {form.id ? 'Actualizar' : 'Crear'}
        </Button>
        {form.id && (
          <Button {...bw.btnGhost} onClick={resetForm}>
            Cancelar
          </Button>
        )}
        <Button {...bw.btnGhost} leftIcon={<FiRefreshCw />} onClick={load}>
          Refrescar
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: tableBorderColor } }}
            border="1px solid"
            borderColor={tableBorderColor}
          >
            <Thead bg={theadBg}>
              <Tr>
                <Th borderColor={tableBorderColor}>ID</Th>
                <Th borderColor={tableBorderColor}>Título</Th>
                <Th borderColor={tableBorderColor}>Fecha</Th>
                <Th borderColor={tableBorderColor}>Key</Th>
                <Th borderColor={tableBorderColor}>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {events.map((e) => (
                <Tr key={e.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor={tableBorderColor}>{e.id}</Td>
                  <Td borderColor={tableBorderColor}>{e.title}</Td>
                  <Td borderColor={tableBorderColor}>
                    {e.date || ''}
                    {e.end_date && e.end_date !== e.date && ` - ${e.end_date}`}
                  </Td>
                  <Td borderColor={tableBorderColor}>{(e.key || `${e.date || ''}|${e.title || ''}`).trim()}</Td>
                  <Td borderColor={tableBorderColor} whiteSpace="nowrap">
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      mr={2}
                      leftIcon={<FiEdit2 />}
                      onClick={() => editRow(e)}
                    >
                      Editar
                    </Button>
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      leftIcon={<FiTrash2 />}
                      onClick={() => remove(e.id)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
              {events.length === 0 && (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={4} borderColor={tableBorderColor}>
                    No hay eventos registrados.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
